/**
 * Procedural item art. Every icon is a small SVG (64×64 viewBox) generated from
 * the item's kind, slot and colour, then cached as a data URL so it renders
 * crisply at any size / DPR. Icons have transparent backgrounds and sit on the
 * rarity gradient of the cell (`.rbg-*`).
 */
import type { StoneItem } from '../../game/items';
import { type EquipItem, type GearSlot, type ItemDef, ITEMS, RARITY_HUE } from '../../game/items';

type Shape =
  | 'greatsword'
  | 'dagger'
  | 'staff'
  | 'pistol'
  | 'gauntlet'
  | 'lyre'
  | 'head'
  | 'shoulders'
  | 'chest'
  | 'pants'
  | 'gloves'
  | 'necklace'
  | 'earring'
  | 'ring'
  | 'stone'
  | 'gem'
  | 'book'
  | 'flask'
  | 'vial'
  | 'grenade'
  | 'bomb'
  | 'banner'
  | 'campfire'
  | 'shards'
  | 'crystal'
  | 'ore'
  | 'ingot'
  | 'herb'
  | 'log'
  | 'fish'
  | 'relic'
  | 'pelt'
  | 'coin'
  | 'scroll'
  | 'feather'
  | 'card'
  | 'chest_box'
  | 'orb';

// ---------------------------------------------------------------- colour helpers

const hex = (n: number): string => `#${(n & 0xffffff).toString(16).padStart(6, '0')}`;
function mix(a: number, b: number, t: number): string {
  const ch = (s: number) => Math.round(((a >> s) & 255) * (1 - t) + ((b >> s) & 255) * t);
  return `rgb(${ch(16)},${ch(8)},${ch(0)})`;
}
const light = (c: number, t: number): string => mix(c, 0xffffff, t);
const dark = (c: number, t: number): string => mix(c, 0x000000, t);

const O = 'stroke="#0a0c0f" stroke-width="1.5" stroke-linejoin="round"';

function defs(h: number): string {
  return `<defs>
<linearGradient id="m" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#f6f8fb"/><stop offset=".45" stop-color="#b3bdc7"/><stop offset="1" stop-color="#4f5a66"/></linearGradient>
<linearGradient id="t" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${light(h, 0.55)}"/><stop offset=".5" stop-color="${hex(h)}"/><stop offset="1" stop-color="${dark(h, 0.55)}"/></linearGradient>
<radialGradient id="g" cx=".35" cy=".3" r=".8"><stop offset="0" stop-color="#fff"/><stop offset=".25" stop-color="${light(h, 0.4)}"/><stop offset=".7" stop-color="${hex(h)}"/><stop offset="1" stop-color="${dark(h, 0.5)}"/></radialGradient>
<radialGradient id="b" cx=".35" cy=".3" r=".8"><stop offset="0" stop-color="${light(h, 0.3)}"/><stop offset=".45" stop-color="${dark(h, 0.35)}"/><stop offset="1" stop-color="${dark(h, 0.82)}"/></radialGradient>
<linearGradient id="au" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#fff1c2"/><stop offset=".5" stop-color="#d8b25a"/><stop offset="1" stop-color="#7a5a1c"/></linearGradient>
<linearGradient id="w" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#9a6a3c"/><stop offset="1" stop-color="#4a2c16"/></linearGradient>
<linearGradient id="rk" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#9a948a"/><stop offset=".5" stop-color="#58524a"/><stop offset="1" stop-color="#2a2622"/></linearGradient>
<linearGradient id="st" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#7a7288"/><stop offset=".5" stop-color="#3d3548"/><stop offset="1" stop-color="#1c1824"/></linearGradient>
<linearGradient id="fire" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#ffe9a0"/><stop offset=".5" stop-color="#ff8a2a"/><stop offset="1" stop-color="#b8321a"/></linearGradient>
<filter id="s" x="-25%" y="-25%" width="150%" height="150%"><feDropShadow dx="0" dy="1.6" stdDeviation="1.3" flood-color="#000" flood-opacity=".75"/></filter>
<filter id="gl" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="2.2"/></filter>
</defs>`;
}

const sparkle = (x: number, y: number, r = 3, o = 0.9): string =>
  `<path d="M${x} ${y - r} L${x + r * 0.35} ${y - r * 0.35} L${x + r} ${y} L${x + r * 0.35} ${y + r * 0.35} L${x} ${y + r} L${x - r * 0.35} ${y + r * 0.35} L${x - r} ${y} L${x - r * 0.35} ${y - r * 0.35} Z" fill="#fff" opacity="${o}"/>`;

/** Draws a stroke path with a dark outline underneath (for strokes that act as shapes). */
const outlined = (d: string, paint: string, w: number, extra = ''): string =>
  `<path d="${d}" fill="none" stroke="#0a0c0f" stroke-width="${w + 2.4}" stroke-linecap="round" stroke-linejoin="round"/><path d="${d}" fill="none" stroke="${paint}" stroke-width="${w}" stroke-linecap="round" stroke-linejoin="round" ${extra}/>`;

// ---------------------------------------------------------------- shapes

function blade(): string {
  return `<path d="M32 3 L37 12 L37 40 L27 40 L27 12 Z" fill="url(#m)" ${O}/>
<path d="M32 7 L32 39" stroke="#fff" stroke-opacity=".6" stroke-width="1.2"/>
<rect x="18" y="39.5" width="28" height="5" rx="2.2" fill="url(#t)" ${O}/>
<rect x="29.4" y="44.5" width="5.2" height="11.5" rx="1.2" fill="#3b2a1d" ${O}/>
<path d="M29.6 47.5 h4.8 M29.6 50.5 h4.8 M29.6 53.5 h4.8" stroke="#8a6a48" stroke-width="1"/>
<circle cx="32" cy="58.6" r="3.6" fill="url(#t)" ${O}/>`;
}

function knife(): string {
  return `<path d="M32 6 L35.5 13 L35.5 36 L28.5 36 L28.5 13 Z" fill="url(#m)" ${O}/>
<path d="M32 9 V35" stroke="#fff" stroke-opacity=".55" stroke-width="1"/>
<rect x="24" y="35.5" width="16" height="4" rx="1.8" fill="url(#t)" ${O}/>
<rect x="30" y="39.5" width="4" height="10" rx="1" fill="#3b2a1d" ${O}/>
<circle cx="32" cy="51.5" r="2.8" fill="url(#t)" ${O}/>`;
}

function gloves(spiked: boolean): string {
  const spikes = spiked ? `<path d="M21 25 L24.5 15 L28 25 Z M29 25 L32.5 13.5 L36 25 Z M37 25 L40.5 15 L44 25 Z" fill="url(#t)" ${O}/>` : '';
  return `<rect x="19" y="10" width="6.2" height="18" rx="3" fill="url(#m)" ${O}/>
<rect x="26" y="7" width="6.2" height="20" rx="3" fill="url(#m)" ${O}/>
<rect x="33" y="8" width="6.2" height="19" rx="3" fill="url(#m)" ${O}/>
<rect x="40" y="12" width="5.8" height="16" rx="2.8" fill="url(#m)" ${O}/>
<path d="M19.5 18 h5 M26.5 16 h5 M33.5 17 h5 M40.5 20 h4.8" stroke="#4e5a66" stroke-width="1"/>
<path d="M19 31 L11 25 Q8 22.5 10 20 Q12 18 15 20 L23 26 Z" fill="url(#m)" ${O}/>
<path d="M18 24 H46 V40 Q46 44.5 42 45 H22 Q18 44.5 18 40 Z" fill="url(#m)" ${O}/>
<path d="M20 27.5 H44" stroke="#4e5a66" stroke-width="1.3"/>
<path d="M22 31 Q23 36 28 37" stroke="#fff" stroke-opacity=".5" stroke-width="1.3" fill="none" stroke-linecap="round"/>
${spikes}
<path d="M19 44 H45 L48 58 H16 Z" fill="url(#t)" ${O}/>
<path d="M18 50 H46" stroke="#000" stroke-opacity=".3" stroke-width="1.2"/>`;
}

function body(shape: Shape, h: number): string {
  switch (shape) {
    case 'greatsword':
      return `<g transform="rotate(45 32 32)">${blade()}</g>`;
    case 'dagger':
      return `<g transform="rotate(-30 32 34)">${knife()}</g><g transform="rotate(30 32 34)">${knife()}</g>`;
    case 'staff':
      return `${outlined('M12 58 L41 23', 'url(#w)', 4)}
<rect x="36" y="25" width="7" height="3.2" rx="1" transform="rotate(-50 39.5 26.6)" fill="url(#au)" ${O}/>
${outlined('M40 25 C34 20 36 10 44 7', 'url(#t)', 2.6)}${outlined('M42 25 C50 26 56 18 55 9', 'url(#t)', 2.6)}
<circle cx="46" cy="15" r="9" fill="${hex(h)}" opacity=".6" filter="url(#gl)"/>
<circle cx="46" cy="15" r="7" fill="url(#g)" ${O}/>${sparkle(43.5, 12.5, 2.4)}`;
    case 'pistol':
      return `<g transform="rotate(-14 34 34)">
<path d="M22 27 L38 27 L38 31 L33 31 Q31 33 31 36 L29 52 Q28.5 55 25 55 L18 55 Q15.5 55 16 52 L20 34 Q21 30 22 27 Z" fill="url(#w)" ${O}/>
<path d="M33 31 Q34 38.5 41 36.5 L41 31" fill="none" stroke="#14171b" stroke-width="2"/>
<rect x="18" y="19" width="39" height="9" rx="2" fill="url(#m)" ${O}/>
<rect x="22" y="21.4" width="30" height="2.4" fill="url(#t)"/>
<rect x="53" y="20.5" width="5" height="6" rx="1" fill="#2a2f36" ${O}/>
<path d="M17 21 L20 15.5 L23.5 18.5 L22 20" fill="#2a2f36" ${O}/>
<path d="M24 33 L22 47" stroke="#c9a06a" stroke-opacity=".5" stroke-width="1.2"/></g>`;
    case 'gauntlet':
      return gloves(true);
    case 'lyre':
      return `${outlined('M22 53 C12 44 10 27 16 15 C18 10.5 22 9.5 24 12.5', 'url(#au)', 4.2)}${outlined('M42 53 C52 44 54 27 48 15 C46 10.5 42 9.5 40 12.5', 'url(#au)', 4.2)}
${outlined('M17 20 H47', 'url(#au)', 3.4)}
<path d="M25.5 21.5 V50 M29.8 21.5 V50 M34.2 21.5 V50 M38.5 21.5 V50" stroke="#fff6e0" stroke-width=".9"/>
<rect x="18.5" y="49" width="27" height="7.5" rx="3" fill="url(#t)" ${O}/>
<circle cx="32" cy="20" r="3" fill="url(#g)" ${O}/>`;
    case 'head':
      return `<path d="M14 40 C14 20 22 9 32 9 C42 9 50 20 50 40 L50 50 Q50 55 45 56 L19 56 Q14 55 14 50 Z" fill="url(#m)" ${O}/>
<path d="M18 29.5 H46 V34.5 H36 V52 H28 V34.5 H18 Z" fill="#101318" ${O}/>
<path d="M15.5 49.5 H27.5 M36.5 49.5 H48.5" stroke="url(#t)" stroke-width="2.6"/>
<path d="M32 2.5 C36.5 8 36.5 14 32 21 C27.5 14 27.5 8 32 2.5 Z" fill="url(#t)" ${O}/>
<path d="M20 26 C21 18 26 13.5 30.5 12.5" fill="none" stroke="#fff" stroke-opacity=".65" stroke-width="1.5" stroke-linecap="round"/>
<circle cx="18.5" cy="43" r="1.4" fill="url(#au)"/><circle cx="45.5" cy="43" r="1.4" fill="url(#au)"/>`;
    case 'shoulders':
      return `<path d="M15 50 C26 43 41 43 52 46 L50 53 C40 49.5 29 49.5 18 56 Z" fill="url(#m)" ${O}/>
<path d="M10 40 C24 32 42 31 56 35 L54 42.5 C42 38.5 26 38.5 13 46.5 Z" fill="url(#m)" ${O}/>
<path d="M8 34 C9 18 26 8 44 10 C54 12 58 20 57 30 C44 25 24 26 8 34 Z" fill="url(#m)" ${O}/>
<path d="M8.6 33.5 C24 26 44 25 56.6 29.6" fill="none" stroke="url(#t)" stroke-width="2.6"/>
<path d="M16 24 C24 15 36 12 46 13" fill="none" stroke="#fff" stroke-opacity=".6" stroke-width="1.4" stroke-linecap="round"/>
<circle cx="21" cy="32.2" r="1.5" fill="url(#au)"/><circle cx="32.5" cy="29.4" r="1.5" fill="url(#au)"/><circle cx="44" cy="29.4" r="1.5" fill="url(#au)"/>`;
    case 'chest':
      return `<path d="M20 8 L27 7 Q32 13 37 7 L44 8 L54 16 L49 28 L50 50 Q32 60 14 50 L15 28 L10 16 Z" fill="url(#m)" ${O}/>
<path d="M32 13.5 V56" stroke="#56616d" stroke-width="1.2"/><path d="M33 13.5 V56" stroke="#fff" stroke-opacity=".35"/>
<path d="M18 28 Q25 34.5 32 28 Q39 34.5 46 28" fill="none" stroke="#4e5a66" stroke-width="1.4"/>
<path d="M15 43.5 Q32 50 49 43.5 L49.4 49 Q32 56 14.6 49 Z" fill="url(#t)" ${O}/>
<path d="M10.5 16 L20 8.5 M53.5 16 L44 8.5" stroke="url(#t)" stroke-width="2.6" stroke-linecap="round"/>
<path d="M19 21 Q20 15 25 12.5" stroke="#fff" stroke-opacity=".6" stroke-width="1.4" fill="none" stroke-linecap="round"/>`;
    case 'pants':
      return `<path d="M15 12 H31 L30 36 L28.5 58 H18 L16.5 36 Z" fill="url(#m)" ${O}/>
<path d="M33 12 H49 L47.5 36 L46 58 H35.5 L34 36 Z" fill="url(#m)" ${O}/>
<rect x="13" y="6" width="38" height="7" rx="2" fill="url(#t)" ${O}/>
<ellipse cx="23.2" cy="35" rx="5.6" ry="4.3" fill="url(#t)" ${O}/><ellipse cx="40.8" cy="35" rx="5.6" ry="4.3" fill="url(#t)" ${O}/>
<path d="M19.5 16 V29 M37.5 16 V29" stroke="#fff" stroke-opacity=".5" stroke-width="1.3" stroke-linecap="round"/>
<path d="M18 46 H28 M36 46 H46" stroke="#4e5a66" stroke-width="1.2"/>`;
    case 'gloves':
      return gloves(false);
    case 'necklace':
      return `<path d="M12 7 C12 30 22 40 32 41 C42 40 52 30 52 7" fill="none" stroke="#5a4418" stroke-width="3.4" stroke-linecap="round"/>
<path d="M12 7 C12 30 22 40 32 41 C42 40 52 30 52 7" fill="none" stroke="#f0d589" stroke-width="2" stroke-dasharray="2.4 1.8" stroke-linecap="round"/>
<circle cx="32" cy="41" r="2.6" fill="none" stroke="#e8c877" stroke-width="1.6"/>
<path d="M32 42.5 L42.5 50.5 L32 62 L21.5 50.5 Z" fill="url(#au)" ${O}/>
<path d="M32 45.5 L39 50.5 L32 58.5 L25 50.5 Z" fill="url(#g)"/>
<path d="M25 50.5 H39 M32 45.5 V58.5" stroke="#fff" stroke-opacity=".35" stroke-width=".8"/>${sparkle(29, 48.5, 2)}`;
    case 'earring':
      return `<path d="M37 8 C29 3 23.5 12 30 16.5 L32 20" fill="none" stroke="#5a4418" stroke-width="3.6" stroke-linecap="round"/>
<path d="M37 8 C29 3 23.5 12 30 16.5 L32 20" fill="none" stroke="#f0d589" stroke-width="2" stroke-linecap="round"/>
<circle cx="32" cy="22.5" r="3.6" fill="url(#au)" ${O}/>
<path d="M32 25 L41.5 38 L32 61 L22.5 38 Z" fill="url(#g)" ${O}/>
<path d="M22.5 38 H41.5 M32 25 V61 M27 38 L32 61 M37 38 L32 61" stroke="#fff" stroke-opacity=".3" stroke-width=".8"/>${sparkle(28, 32, 3)}`;
    case 'ring':
      return `<ellipse cx="32" cy="42" rx="18" ry="15" fill="none" stroke="#0a0c0f" stroke-width="8.6"/>
<ellipse cx="32" cy="42" rx="18" ry="15" fill="none" stroke="url(#au)" stroke-width="6"/>
<path d="M17 38 C19 31 25 27.5 31 27" fill="none" stroke="#fff6d6" stroke-opacity=".75" stroke-width="1.4" stroke-linecap="round"/>
<path d="M22 27 L27 17.5 H37 L42 27 Z" fill="url(#au)" ${O}/>
<circle cx="32" cy="17.5" r="8.6" fill="url(#g)" ${O}/>
<circle cx="24.3" cy="17.5" r="1.7" fill="#f0d589"/><circle cx="39.7" cy="17.5" r="1.7" fill="#f0d589"/><circle cx="32" cy="9.2" r="1.7" fill="#f0d589"/>${sparkle(29, 14.5, 2.6)}`;
    case 'stone': {
      const runes = 'M32 18 V45 M32 24 L25.5 18.5 M32 24 L38.5 18.5 M32 37 L25.5 42.5 M32 37 L38.5 42.5 M23 30.5 H41';
      return `<path d="M32 4 L52 15 L55 43 L33 60 L11 47 L9 18 Z" fill="url(#st)" ${O}/>
<path d="M32 12 L46 19 L47 41 L33 50 L18 42 L18 20 Z" fill="#fff" fill-opacity=".07"/>
<path d="M32 4 L32 12 L46 19 L52 15 M32 12 L18 20 L9 18 M18 20 L18 42 L11 47 M18 42 L33 50 L33 60 M33 50 L47 41 L55 43 M47 41 L46 19" fill="none" stroke="#000" stroke-opacity=".3" stroke-width="1"/>
<path d="M32 4.6 L51.4 15.2" stroke="#fff" stroke-opacity=".35" stroke-width="1.1"/>
<path d="${runes}" stroke="${light(h, 0.2)}" stroke-width="3.2" fill="none" stroke-linecap="round" filter="url(#gl)"/>
<path d="${runes}" stroke="${light(h, 0.65)}" stroke-width="1.5" fill="none" stroke-linecap="round"/>`;
    }
    case 'gem':
      return `<ellipse cx="32" cy="33" rx="22" ry="22" fill="${hex(h)}" opacity=".32" filter="url(#gl)"/>
<path d="M32 6 L52 18 L52 44 L32 58 L12 44 L12 18 Z" fill="${dark(h, 0.35)}"/>
<path d="M32 6 L32 16 L21 23 L12 18 Z" fill="${light(h, 0.55)}"/>
<path d="M32 6 L52 18 L43 23 L32 16 Z" fill="${light(h, 0.3)}"/>
<path d="M52 18 L52 44 L43 39 L43 23 Z" fill="${dark(h, 0.15)}"/>
<path d="M52 44 L32 58 L32 46 L43 39 Z" fill="${dark(h, 0.5)}"/>
<path d="M32 58 L12 44 L21 39 L32 46 Z" fill="${dark(h, 0.3)}"/>
<path d="M12 44 L12 18 L21 23 L21 39 Z" fill="${light(h, 0.2)}"/>
<path d="M32 16 L43 23 L43 39 L32 46 L21 39 L21 23 Z" fill="url(#g)"/>
<path d="M32 6 L52 18 L52 44 L32 58 L12 44 L12 18 Z" fill="none" ${O}/>${sparkle(25, 21, 3.4)}`;
    case 'book':
      return `<path d="M18 12 L48 8 L52 52 L22 57 Z" fill="#e9dfc6" ${O}/>
<path d="M22 55 L51 50.5 M21.6 53 L50.8 48.4" stroke="#b9ab88" stroke-width=".8"/>
<path d="M14 11 L46 6 L49 50 L17 55 Z" fill="url(#t)" ${O}/>
<path d="M14 11 L17 55 L12.5 56 L9.5 12 Z" fill="${dark(h, 0.6)}" ${O}/>
<path d="M18.2 14.2 L42.8 10.2 L45.3 47 L20.7 51 Z" fill="none" stroke="#e8c877" stroke-opacity=".8" stroke-width="1.1"/>
<path d="M31.5 21 L39 30.5 L31.5 40 L24 30.5 Z" fill="${light(h, 0.5)}" filter="url(#gl)"/>
<path d="M31.5 22.5 L37.6 30.5 L31.5 38.5 L25.4 30.5 Z" fill="#fff8e8" ${O}/>
<path d="M31.5 26 L34.4 30.5 L31.5 35 L28.6 30.5 Z" fill="${hex(h)}"/>
<rect x="45.5" y="25" width="6" height="9" rx="1.6" fill="url(#au)" ${O}/>`;
    case 'flask':
      return `<rect x="26.5" y="11" width="11" height="14" fill="#d6ecf3" fill-opacity=".3" ${O}/>
<rect x="25" y="3.5" width="14" height="8.5" rx="2" fill="url(#w)" ${O}/>
<circle cx="32" cy="41" r="18" fill="#d6ecf3" fill-opacity=".16" ${O}/>
<clipPath id="c"><circle cx="32" cy="41" r="16.8"/></clipPath>
<g clip-path="url(#c)"><path d="M10 38 Q21 33.5 32 38 Q43 42.5 54 37 V62 H10 Z" fill="url(#t)"/><path d="M10 38 Q21 33.5 32 38 Q43 42.5 54 37" fill="none" stroke="${light(h, 0.6)}" stroke-width="1.2"/></g>
<circle cx="26" cy="47" r="1.8" fill="#fff" opacity=".7"/><circle cx="36" cy="51" r="1.2" fill="#fff" opacity=".6"/><circle cx="33" cy="45" r=".9" fill="#fff" opacity=".7"/>
<path d="M19.5 35 A13.5 13.5 0 0 1 27.5 26.5" stroke="#fff" stroke-opacity=".8" stroke-width="2.2" fill="none" stroke-linecap="round"/>
<rect x="25" y="22.5" width="14" height="3" rx="1" fill="#d6ecf3" fill-opacity=".5" ${O}/>`;
    case 'vial':
      return `<rect x="24.5" y="3.5" width="15" height="7" rx="2" fill="url(#au)" ${O}/>
<rect x="25.5" y="10" width="13" height="47" rx="6.5" fill="#d6ecf3" fill-opacity=".16" ${O}/>
<rect x="27" y="22" width="10" height="33.5" rx="5" fill="url(#t)"/>
<circle cx="32" cy="38" r="9" fill="${hex(h)}" opacity=".55" filter="url(#gl)"/>
<path d="M34 25 L28.5 37 H32.5 L29.5 50 L36 35 H32 Z" fill="#fffbe0" ${O}/>
<path d="M28 14 V30" stroke="#fff" stroke-opacity=".7" stroke-width="1.6" stroke-linecap="round"/>`;
    case 'grenade':
      return `<circle cx="31" cy="39" r="17" fill="url(#b)" ${O}/>
<path d="M14.4 40 Q31 47.5 47.6 40" fill="none" stroke="#0a0c0f" stroke-width="3.6"/><path d="M14.4 40 Q31 47.5 47.6 40" fill="none" stroke="url(#au)" stroke-width="2"/>
<ellipse cx="24.5" cy="31" rx="5" ry="3.2" transform="rotate(-35 24.5 31)" fill="#fff" opacity=".35"/>
<rect x="26" y="17" width="10" height="7" rx="1.5" fill="url(#m)" ${O}/>
<path d="M31 17 C31 11 36 8 41 9" fill="none" stroke="#6b4a2a" stroke-width="2.2" stroke-linecap="round"/>
<circle cx="42" cy="8.5" r="5.5" fill="#ffd36b" opacity=".65" filter="url(#gl)"/>${sparkle(42, 8.5, 4.4, 1)}`;
    case 'bomb':
      return `<path d="M32 17 L49 26.5 L49 47 L32 57 L15 47 L15 26.5 Z" fill="url(#b)" ${O}/>
<path d="M15 26.5 L32 36 L49 26.5 M32 36 V57" fill="none" stroke="#000" stroke-opacity=".35" stroke-width="1.1"/>
<path d="M16.5 27.5 L31.5 19" stroke="#fff" stroke-opacity=".4" stroke-width="1.2"/>
<circle cx="32" cy="41" r="5.2" fill="${light(h, 0.35)}" filter="url(#gl)"/><circle cx="32" cy="41" r="3.6" fill="${light(h, 0.6)}" ${O}/>
<rect x="28" y="12" width="8" height="6" rx="1.5" fill="url(#m)" ${O}/>
<path d="M32 12 C32 7 36 5 40 6" fill="none" stroke="#6b4a2a" stroke-width="2.2" stroke-linecap="round"/>${sparkle(41, 5.5, 3.6, 1)}`;
    case 'banner':
      return `${outlined('M16 60 V7', 'url(#au)', 2.6)}<circle cx="16" cy="5.5" r="3" fill="url(#au)" ${O}/>
<path d="M18 9 L52 12 L44 22 L52 32 L18 31 Z" fill="url(#t)" ${O}/>
<path d="M18.5 10 L50 12.8" stroke="#fff" stroke-opacity=".4" stroke-width="1.1"/>
<path d="M31 14 L35 20.5 L31 27 L27 20.5 Z" fill="#fff" opacity=".85"/>`;
    case 'campfire':
      return `${outlined('M14 55 L50 45', 'url(#w)', 4.6)}${outlined('M50 55 L14 45', 'url(#w)', 4.6)}
<ellipse cx="32" cy="36" rx="14" ry="14" fill="#ff8a2a" opacity=".45" filter="url(#gl)"/>
<path d="M32 6 C40 16 48 24 46 36 C45 44 38 48 32 48 C26 48 19 44 18 36 C17 26 26 22 26 14 C30 18 30 22 32 24 C34 18 34 12 32 6 Z" fill="url(#fire)" ${O}/>
<path d="M32 22 C36 28 40 32 39 38 C38 43 35 45 32 45 C29 45 26 43 25.5 38 C25 33 29 31 30 26 C31 29 31.5 30 32 22 Z" fill="#fff2b0"/>`;
    case 'shards':
      return `<ellipse cx="32" cy="50" rx="20" ry="6" fill="${hex(h)}" opacity=".45" filter="url(#gl)"/>
<path d="M17 22 L24 17 L29 25 L27 50 L19 53 L14 44 Z" fill="${dark(h, 0.2)}" ${O}/><path d="M17 22 L24 17 L22 50 L19 53 L14 44 Z" fill="${light(h, 0.25)}"/>
<path d="M45 25 L51 31 L49 51 L43 55 L38 50 L40 29 Z" fill="${dark(h, 0.25)}" ${O}/><path d="M45 25 L40 29 L38 50 L43 55 Z" fill="${light(h, 0.3)}"/>
<path d="M32 4 L39.5 14 L38.5 50 L32 58 L25.5 50 L24.5 14 Z" fill="${dark(h, 0.1)}" ${O}/><path d="M32 4 L32 58 L25.5 50 L24.5 14 Z" fill="${light(h, 0.45)}"/>
<path d="M28 14 V44" stroke="#fff" stroke-opacity=".7" stroke-width="1.3" stroke-linecap="round"/>${sparkle(36, 12, 3)}`;
    case 'crystal':
      return `<ellipse cx="32" cy="34" rx="16" ry="24" fill="${hex(h)}" opacity=".4" filter="url(#gl)"/>
<path d="M32 3 L45 24 L32 61 L19 24 Z" fill="${dark(h, 0.25)}" ${O}/>
<path d="M32 3 L32 61 L19 24 Z" fill="${light(h, 0.4)}"/><path d="M32 3 L45 24 L32 30 Z" fill="${light(h, 0.15)}"/>
<path d="M19 24 L32 30 L45 24" fill="none" stroke="#fff" stroke-opacity=".5" stroke-width="1"/>
<path d="M32 3 L45 24 L32 61 L19 24 Z" fill="none" ${O}/>${sparkle(26, 18, 3)}`;
    case 'ore':
      return `<path d="M9 41 L15 24 L28 15.5 L43 17.5 L55 30 L53 46 L38 55 L20 53 Z" fill="url(#rk)" ${O}/>
<path d="M15 24 L26 30 L28 15.5 M26 30 L38 34 L43 17.5 M38 34 L53 46 M26 30 L20 53" fill="none" stroke="#000" stroke-opacity=".28" stroke-width="1"/>
<g filter="url(#gl)" fill="${light(h, 0.25)}"><path d="M22 30 L27 25 L31 31 L26 36 Z"/><path d="M38 37 L44 31 L47 38 L41 42 Z"/><path d="M30 43 L34 39 L36 44 L32 47 Z"/></g>
<g fill="${light(h, 0.55)}" ${O.replace('1.5', '1')}><path d="M22 30 L27 25 L31 31 L26 36 Z"/><path d="M38 37 L44 31 L47 38 L41 42 Z"/><path d="M30 43 L34 39 L36 44 L32 47 Z"/></g>
<path d="M17 25 L28 17" stroke="#fff" stroke-opacity=".3" stroke-width="1.2"/>`;
    case 'ingot':
      return `<path d="M10 37 L36 30 L54 37 L28 44 Z" fill="${light(h, 0.35)}" ${O}/><path d="M10 37 L28 44 L28 52 L10 45 Z" fill="${hex(h)}" ${O}/><path d="M28 44 L54 37 L54 45 L28 52 Z" fill="${dark(h, 0.35)}" ${O}/>
<path d="M16 24 L38 18 L52 24 L30 30 Z" fill="${light(h, 0.45)}" ${O}/><path d="M16 24 L30 30 L30 37 L16 31 Z" fill="${light(h, 0.05)}" ${O}/><path d="M30 30 L52 24 L52 31 L30 37 Z" fill="${dark(h, 0.3)}" ${O}/>
<path d="M33 21.5 L36 23.5 L33 25.5 L30 23.5 Z" fill="${dark(h, 0.3)}"/><path d="M18 24.4 L37.5 19" stroke="#fff" stroke-opacity=".6" stroke-width="1"/>`;
    case 'herb':
      return `<path d="M32 60 C31 48 30 36 33 22" stroke="#2f5a22" stroke-width="2.6" fill="none" stroke-linecap="round"/>
<path d="M32 41 C20 41 12 31 12 20 C24 20 32 28 32 41 Z" fill="url(#t)" ${O}/><path d="M31.5 40 C25 34 19 27 13.5 21" stroke="#fff" stroke-opacity=".4" fill="none" stroke-width="1"/>
<path d="M33 33 C44 35 52 27 54 15 C42 13 34 21 33 33 Z" fill="url(#t)" ${O}/><path d="M33.5 32 C40 27 46 21 53 16" stroke="#fff" stroke-opacity=".4" fill="none" stroke-width="1"/>
<path d="M33 23 C29.5 15 31.5 9 36 4.5 C40.5 10.5 38.5 17.5 33 23 Z" fill="url(#t)" ${O}/>`;
    case 'log':
      return `<g transform="rotate(-24 32 34)">
<path d="M12 25.5 H50 V42.5 H12 A4.5 8.5 0 0 1 12 25.5 Z" fill="url(#w)" ${O}/>
<path d="M17 30 H33 M21 36.5 H44 M16 39.5 H27 M36 29 H46" stroke="#2e1c0e" stroke-width="1.2" stroke-linecap="round"/>
<ellipse cx="50" cy="34" rx="5" ry="8.5" fill="#dcb47c" ${O}/>
<ellipse cx="50" cy="34" rx="3.1" ry="5.4" fill="none" stroke="#9a6a3a" stroke-width="1"/><ellipse cx="50" cy="34" rx="1.3" ry="2.4" fill="none" stroke="#9a6a3a" stroke-width="1"/></g>
<path d="M22 50 C20 45 23 42 27 43" stroke="#4f8a34" stroke-width="2" fill="none" stroke-linecap="round"/>`;
    case 'fish':
      return `<g transform="rotate(-18 32 32)">
<path d="M13 32 L2.5 21.5 L5 32 L2.5 42.5 Z" fill="${dark(h, 0.3)}" ${O}/>
<path d="M28 21 L35 11.5 L40.5 22 Z" fill="${dark(h, 0.3)}" ${O}/>
<path d="M10 32 C18 18 40 16 56 30 C58 32 58 33 56 34 C40 48 18 46 10 32 Z" fill="url(#t)" ${O}/>
<path d="M42 23.5 C39 30 39 35 42 41" fill="none" stroke="#000" stroke-opacity=".35" stroke-width="1.2"/>
<path d="M18 28.5 C27 24 36 23.5 44 25.5" stroke="#fff" stroke-opacity=".5" stroke-width="1.4" fill="none" stroke-linecap="round"/>
<circle cx="48.5" cy="29" r="2.5" fill="#fff"/><circle cx="49" cy="29" r="1.3" fill="#111"/></g>`;
    case 'relic':
      return `<path d="M14 8 H44 L50 16 L47.5 30 L52 38 L44 46.5 L46 58 H16 L12 48 L16 36 L11 24 Z" fill="#b89e72" ${O}/>
<path d="M14 8 H44 L50 16 L47.5 30 L52 38 L44 46.5 L46 58 H16 L12 48 L16 36 L11 24 Z" fill="url(#rk)" opacity=".35"/>
<circle cx="30" cy="31" r="9" fill="none" stroke="${light(h, 0.2)}" stroke-width="3" filter="url(#gl)"/>
<g stroke="${light(h, 0.55)}" stroke-width="1.7" fill="none" stroke-linecap="round"><circle cx="30" cy="31" r="9"/><path d="M30 18 V44 M17.5 31 H42.5 M24 25 L36 37"/></g>
<path d="M15.5 10 H43" stroke="#fff" stroke-opacity=".4" stroke-width="1.1"/>`;
    case 'pelt':
      return `<path d="M20 10 C24 14 28 14 32 12 C36 14 40 14 44 10 L48 18 C46 22 46 26 50 30 L56 34 L50 38 C46 42 46 46 48 50 L44 58 C40 54 36 54 32 56 C28 54 24 54 20 58 L16 50 C18 46 18 42 14 38 L8 34 L14 30 C18 26 18 22 16 18 Z" fill="url(#t)" ${O}/>
<path d="M32 16 V52" stroke="#000" stroke-opacity=".22" stroke-width="1.4"/>
<path d="M24 22 l2 3 M38 22 l-2 3 M22 34 l3 1 M42 34 l-3 1 M25 45 l2 -3 M39 45 l-2 -3" stroke="#fff" stroke-opacity=".3" stroke-width="1.2" stroke-linecap="round"/>`;
    case 'coin':
      return `<circle cx="32" cy="32" r="24" fill="url(#g)" ${O}/>
<circle cx="32" cy="32" r="19" fill="none" stroke="#000" stroke-opacity=".32" stroke-width="1.6"/>
<circle cx="32" cy="32" r="20.4" fill="none" stroke="#fff" stroke-opacity=".35" stroke-width="1"/>
<path d="M32 19 L35.5 28 L45 28.5 L37.5 34.5 L40 44 L32 38.8 L24 44 L26.5 34.5 L19 28.5 L28.5 28 Z" fill="${light(h, 0.6)}" stroke="${dark(h, 0.55)}" stroke-width="1.2" stroke-linejoin="round"/>
<path d="M17 22 A18 18 0 0 1 30 13" fill="none" stroke="#fff" stroke-opacity=".6" stroke-width="1.8" stroke-linecap="round"/>`;
    case 'scroll':
      return `<rect x="15" y="15" width="34" height="34" fill="#eadcb8" ${O}/>
<path d="M19 22 H45 M19 42 H45" stroke="#b9a57a" stroke-width="1"/>
<rect x="10" y="9" width="44" height="8" rx="4" fill="#cdb483" ${O}/><rect x="10" y="47" width="44" height="8" rx="4" fill="#cdb483" ${O}/>
<circle cx="10" cy="13" r="3" fill="url(#t)" ${O}/><circle cx="54" cy="13" r="3" fill="url(#t)" ${O}/><circle cx="10" cy="51" r="3" fill="url(#t)" ${O}/><circle cx="54" cy="51" r="3" fill="url(#t)" ${O}/>
<ellipse cx="28.5" cy="38.5" rx="3.6" ry="2.7" transform="rotate(-20 28.5 38.5)" fill="${dark(h, 0.35)}"/><path d="M31.6 38 V26 L39.5 24 V35" fill="none" stroke="${dark(h, 0.35)}" stroke-width="1.8"/><ellipse cx="36.4" cy="35.8" rx="3.3" ry="2.5" transform="rotate(-20 36.4 35.8)" fill="${dark(h, 0.35)}"/>`;
    case 'feather':
      return `<path d="M51 5 C34 11 18 29 16 54 C28 44 45 30 51 5 Z" fill="url(#t)" ${O}/>
<path d="M51 5 C39 20 27 36 12 60" fill="none" stroke="#fff3d6" stroke-width="1.6" stroke-linecap="round"/>
<path d="M43 17 L35 19.5 M38.5 24.5 L28.5 28.5 M33.5 32 L24 37 M28.5 40 L20 45.5" stroke="#000" stroke-opacity=".25" stroke-width="1"/>
<path d="M46 12 C40 20 32 26 26 34" stroke="#fff" stroke-opacity=".35" stroke-width="1.2" fill="none"/>`;
    case 'card':
      return `<rect x="14" y="5" width="36" height="54" rx="3" fill="url(#t)" ${O}/>
<rect x="18" y="9" width="28" height="36" fill="${dark(h, 0.55)}"/>
<circle cx="32" cy="22" r="6.5" fill="${light(h, 0.35)}"/><path d="M21 45 C21 34 43 34 43 45 Z" fill="${light(h, 0.35)}"/>
<rect x="18" y="48" width="28" height="7" fill="${dark(h, 0.3)}"/>`;
    case 'chest_box':
      return `<path d="M10 30 H54 V54 H10 Z" fill="url(#w)" ${O}/>
<path d="M10 30 C10 16 20 10 32 10 C44 10 54 16 54 30 Z" fill="url(#w)" ${O}/>
<path d="M18 13.5 V54 M46 13.5 V54 M10 30 H54" stroke="url(#au)" stroke-width="3"/>
<rect x="27.5" y="26" width="9" height="11" rx="1.5" fill="url(#au)" ${O}/>
<circle cx="32" cy="31.5" r="1.8" fill="#1a1208"/>`;
    case 'orb':
    default:
      return `<circle cx="32" cy="32" r="18" fill="${hex(h)}" opacity=".5" filter="url(#gl)"/><circle cx="32" cy="32" r="15" fill="url(#g)" ${O}/>${sparkle(26, 26, 3.4)}`;
  }
}

// ---------------------------------------------------------------- cache & public API

const cache = new Map<string, string>();

function svgUrl(shape: Shape, hue: number): string {
  const key = `${shape}:${hue}`;
  let url = cache.get(key);
  if (!url) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">${defs(hue)}<g filter="url(#s)">${body(shape, hue)}</g></svg>`;
    url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    cache.set(key, url);
  }
  return url;
}

const WEAPON_SHAPE: Record<string, Shape> = {
  greatsword: 'greatsword',
  sword: 'greatsword',
  dagger: 'dagger',
  staff: 'staff',
  pistol: 'pistol',
  gun: 'pistol',
  gauntlet: 'gauntlet',
  lyre: 'lyre',
  harp: 'lyre',
};

/** Accent hues for gear sets; other gear is tinted by rarity. */
export const SET_HUE: Record<string, number> = { vanguard: 0x6f9fd8, reliquary: 0x3fc9b8, warlord: 0xd8402c };

export function slotShape(slot: GearSlot | 'necklace' | 'earring' | 'ring' | 'stone', weapon = 'greatsword'): Shape {
  if (slot === 'weapon') return WEAPON_SHAPE[weapon] ?? 'greatsword';
  return slot;
}

/** Icon for a unique equipment item. `weapon` is the owning class's weapon kind (ClassDef.weapon). */
export function equipIcon(item: EquipItem, weapon?: string): string {
  if (item.kind === 'stone') return stoneIcon(item);
  const hue = (item.kind === 'gear' && item.set ? SET_HUE[item.set] : undefined) ?? RARITY_HUE[item.rarity];
  return svgUrl(item.kind === 'gear' ? slotShape(item.slot, weapon) : item.slot, hue);
}

export function stoneIcon(stone: StoneItem): string {
  const hue = stone.rarity === 'legendary' ? 0xffa040 : stone.rarity === 'relic' ? 0xff6a2a : stone.rarity === 'rare' ? 0x5ab8ff : 0xb07cff;
  return svgUrl('stone', hue);
}

/** Silhouette used for empty equipment slots (render with the `.bi-ghost` class). */
export function emptySlotIcon(slot: GearSlot | 'necklace' | 'earring' | 'ring' | 'stone', weapon?: string): string {
  return svgUrl(slotShape(slot, weapon), 0x6b7580);
}

function stackShape(def: ItemDef): Shape {
  const id = def.id;
  switch (def.kind) {
    case 'gem':
      return 'gem';
    case 'book':
      return 'book';
    case 'card':
      return 'card';
    case 'chest':
      return 'chest_box';
    case 'token':
      return 'coin';
    case 'skillpotion':
      return 'flask';
    case 'battle': {
      const cat = def.battle?.category;
      if (cat === 'grenade') return 'grenade';
      if (cat === 'bomb') return 'bomb';
      if (id.includes('flag') || id.includes('banner')) return 'banner';
      if (id.includes('campfire')) return 'campfire';
      return 'flask';
    }
    default:
      break;
  }
  if (id === 'forge_tome') return 'book';
  if (id === 'catalyst') return 'vial';
  if (id.includes('shard')) return 'shards';
  if (id.includes('crystal')) return 'crystal';
  if (id.startsWith('ore')) return 'ore';
  if (id.includes('alloy') || id.includes('ingot')) return 'ingot';
  if (id.startsWith('herb')) return 'herb';
  if (id.startsWith('timber')) return 'log';
  if (id.startsWith('fish')) return 'fish';
  if (id.startsWith('relic')) return 'relic';
  if (id === 'hide' || id.includes('pelt')) return 'pelt';
  if (id.includes('plume') || id.includes('feather')) return 'feather';
  if (id.includes('song') || id.includes('scroll')) return 'scroll';
  if (def.kind === 'consumable' || def.kind === 'food') return 'flask';
  return 'orb';
}

/** Icon for a catalog (stackable) item. */
export function stackIcon(def: ItemDef): string {
  return svgUrl(stackShape(def), def.icon.hue);
}

/** Icon for any item: an equipment instance, a catalog def, or a catalog id. */
export function itemIcon(x: EquipItem | ItemDef | string, weapon?: string): string {
  if (typeof x === 'string') {
    const def = ITEMS[x];
    return def ? stackIcon(def) : svgUrl('orb', 0x888888);
  }
  if ('uid' in x) return equipIcon(x, weapon);
  return stackIcon(x);
}

/** Gem art by type and level (brighter at higher levels). */
export function gemIcon(type: 'dmg' | 'cd', level: number): string {
  const def = ITEMS[`gem_${type}_${level}`];
  return def ? stackIcon(def) : svgUrl('gem', type === 'dmg' ? 0xff5a3a : 0x3a9aff);
}
